import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// region Atoms
import { LinkContractList, SelectAllClient } from "@atoms/index";
// endregion Atoms
// region Libraries
import { DialogActions, DialogContent, DialogTitle, TransitionProps, Zoom } from "@libraries/mui";
import { Close } from "@libraries/icons";
import * as _ from "lodash";
// endregion Libraries
// region Store
import utils from "@helpers/utils";
// endregion Store
// region Jotai
import Store, { ContractPermissions } from "@store/jotai/index";
// endregion Jotai
// region Graphql
import {
  ClientQueriesGetAllWithSortedPaginationArgs,
  ClientType,
  ContractQueriesGetAllWithPaginationArgs,
  ContractType,
  ECommandResultStatus,
  EWhereClausesOperators,
  UserType
} from "@store/graphql/helpers/types.graphql";
import {
  clientAllWithSortedPagination,
  TClientAllWithSortedPaginationQueries
} from "@graphql/groups/client/query.graphql";
import { contractAllWithPagination, TContractAllWithPaginationQueries } from "@graphql/groups/contract/query.graphql";
// endregion Graphql
// region Language
import { PermissionMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
// endregion Language
// region Styles
import * as Styled from "./styles";

// endregion Styles

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  userSelected: UserType;
}

// Transition helper component
const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
  ) => <Zoom ref={ref} {...props} />
);

const LinkContractDialog: React.FC<Props> = ({
  open,
  onClose,
  title,
  userSelected
}) => {

  const { t } = useTranslation();
  const { addToast } = useToast();

  const [selectedClient, setSelectedClient] = useState<ClientType>({
    name: "",
    id: ""
  } as ClientType);
  const [clients, setClients] = useState<ClientType[]>([]);
  const [filterClient, setFilterClient] = useState<string>("");
  const [lastFilterClient, setLastFilterClient] = useState<string>("");
  const [selectedContracts, setSelectedContracts] = useState<ContractType[]>([]);
  const [contracts, setContracts] = useState<ContractType[]>([]);

  const [nextPageClients, setNextPageClients] = useState<number>(1);
  const [totalPagesClients, setTotalPagesClients] = useState<number>(0);
  const limitPerPage = 20;

  const [timer, setTimer] = useState<NodeJS.Timeout>();

  const { value: permissionsUsers, setValue: setPermissionsUsers } = Store.usePermissionsUsers();

  // region Get clients with sorted pagination
  const [
    getClients,
    { error: errorClients, data: dataClients }
  ] = useLazyQuery<
    TClientAllWithSortedPaginationQueries,
    ClientQueriesGetAllWithSortedPaginationArgs
  >(clientAllWithSortedPagination(["id", "name", "document", "alias", "externalId"]), {
    fetchPolicy: "network-only"
  });
  // endregion Get clients with pagination
  // region Get contracts
  const [
    getContracts,
    { loading: loadingContracts, error: errorContracts, data: dataContracts }
  ] = useLazyQuery<
    TContractAllWithPaginationQueries,
    ContractQueriesGetAllWithPaginationArgs
  >(
    contractAllWithPagination([
      "id",
      "code",
      "status",
      "contractDate",
      "construction.id",
      "construction.name",
      "construction.address",
      "construction.county",
      "construction.number",
      "construction.complement",
      "construction.client.name"
    ]),
    {
      fetchPolicy: "network-only"
    }
  );
  // endregion Get contracts

  const [loadingClients, setLoadingClients] = useState<boolean>(false);

  /**
   * Load clients with sorted pagination
   */
  const loadClients = useCallback(async () => {

    const response = dataClients?.client.getAllWithSortedPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      setTotalPagesClients(response.pageCount);

      if (nextPageClients > 1) setClients(clients.concat(response.result as ClientType[]));
      else setClients(response.result as ClientType[]);
    }
  }, [dataClients, setClients]);

  /**
   * Load contracts
   */
  const loadContracts = useCallback(async () => {

    const response = dataContracts?.contract.getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {

      const contractsSorted = response.result?.sort((a, b) => (a?.contractDate < b?.contractDate ? 1 : -1));
      const contractsFiltered = contractsSorted?.filter((contract) => {
        const permissionFind = permissionsUsers?.find(
          (permission) => permission.id === contract?.id
        );

        return !permissionFind;
      });

      setContracts(contractsFiltered as ContractType[]);
    }
  }, [dataContracts, setContracts, permissionsUsers]);

  /**
   * Close client dialog and clear states
   */
  const handleClose = useCallback(() => {
    setSelectedClient({ name: "", id: "" } as ClientType);
    setContracts([]);
    onClose();
  }, [onClose]);

  /**
   * Link contracts to user
   */
  const handleLinkContracts = useCallback(() => {

    if (selectedContracts.length === 0) {
      addToast({
        type: "error",
        title: "Nenhum contrato selecionado",
        description:
          "Selecione um contrato para adicionar a lista de permissões"
      });

      return;
    }

    const contractsPermissions: ContractPermissions[] = selectedContracts.map(
      (contract) => ({
        ...contract,
        permissions: [],
        userId: userSelected.id
      })
    );

    setPermissionsUsers([...contractsPermissions, ...permissionsUsers]);
    handleClose();
  }, [
    permissionsUsers,
    selectedContracts,
    setPermissionsUsers,
    userSelected,
    handleClose,
    addToast
  ]);

  // Get contracts when client is selected
  useEffect(() => {
    if (selectedClient?.id) {
      getContracts({
        variables: {
          where: [
            {
              property: "construction.client.id",
              operator: EWhereClausesOperators.EQUALS,
              value: selectedClient.id
            }
          ]
        }
      }).then();
    }
  }, [getContracts, selectedClient]);

  // Filter and pagination clients
  useEffect(() => {

    if (filterClient === "") {
      setClients([]);
    } else if (filterClient.length > 2) {

      if (lastFilterClient !== filterClient) {
        setLastFilterClient(filterClient);
        setNextPageClients(1);
      }

      if (timer) clearTimeout(timer);
      setTimer(setTimeout(() => {

        // Treatment according filter
        // Just numbers = Remove special characters
        // Letters = Pass to uppercase
        const filter = _.isNumber(filterClient)
          ? utils.removeSpecialCharacters(filterClient)
          : filterClient.toUpperCase();

        setLoadingClients(true);
        getClients({
          variables: {
            page: nextPageClients,
            perPage: limitPerPage,
            where: [
              {
                property: "alias|document|externalId",
                operator: EWhereClausesOperators.CONTAINS,
                value: utils.removeSpecialCharacters(filter)
              }
            ]
          }
        }).then().finally(() => setLoadingClients(false));
      }, 1000));
    }
  }, [getClients, nextPageClients, filterClient]);

  // Load clients
  useEffect(() => {
    if (dataClients) loadClients().then();
    if (errorClients) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorClients.message
      });
    }
  }, [loadClients, dataClients, errorClients, addToast]);

  // Load contracts
  useEffect(() => {
    if (dataContracts) loadContracts().then();
    if (errorContracts) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorContracts.message
      });
    }
  }, [addToast, dataContracts, errorContracts, loadContracts]);

  // Memoize component (Contract List)
  const contractList = useMemo(() => (
    <Styled.ContractListContainer>
      <LinkContractList
        contracts={contracts}
        loading={loadingContracts}
        setSelectedContracts={setSelectedContracts}
        selectedContracts={selectedContracts}
      />
    </Styled.ContractListContainer>
  ), [contracts, loadingContracts, selectedContracts]);

  // Control last element of clients list
  const handleLastElement = () => {
    if (nextPageClients < totalPagesClients) {
      setNextPageClients(nextPageClients + 1);
    }
  };

  return (
    <Styled.Container
      open={open}
      onClose={handleClose}
      aria-labelledby="pendencyDialog-title"
      aria-describedby="pendencyDialog-description"
      TransitionComponent={Transition}
    >
      <DialogTitle id="pendencyDialog-title">
        {title}
        <Styled.CustomIconButton aria-label="close" onClick={handleClose}>
          <Close />
        </Styled.CustomIconButton>
      </DialogTitle>
      <DialogContent>
        <SelectAllClient
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          options={clients}
          handleLastElement={handleLastElement}
          loading={loadingClients}
          setFilter={setFilterClient}
        />
        {contractList}
      </DialogContent>
      <DialogActions>
        <Styled.DialogActions>
          <Styled.SubmitContractButton
            text={t(PermissionMessages.linkContractsSelectedText).toUpperCase()}
            type="submit"
            onClick={handleLinkContracts}
          />
        </Styled.DialogActions>
      </DialogActions>
    </Styled.Container>
  );
};

export default LinkContractDialog;
